
import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import { namespace, Getter, State } from "vuex-class"
import CalculationDataType from "@/types/field"

import { CaseColumn } from "@/types/caseColumns"
import {
	CalculationType,
	CalculationSortItemType,
	DataType,
	Insight,
	InsightCalculation,
	InsightCalculationTotal,
	GroupByDateFormat,
	Operator,
	operatorLabels,
	FormattingRule, InsightDetails, InsightSummary
} from "@/types/insights"

import vSelect from "vue-select"

import CalculationExpression from "../CalculationExpression.vue"

import { InsightCalculationItem, TimeSpentCalculation } from "@/types/insights/calculation"
import { TenantFeatures } from "@/types/tenantFeatures"
import { DateClauseType } from "@/types/insights/dateClauseType"
import { TRANSITION_FOCUS_DELAY_MS } from "@/constants/animations"

@Component({
	components: {
		"v-select": vSelect,
		"calculation-expression": CalculationExpression
	}
})
export default class Calculation extends Vue {
	@State tenantEnabledFeatures!: { [key: string]: boolean }

	@Prop({ default: null })
	value!: InsightCalculationItem<InsightCalculation>

	@Prop({ default: null })
	insight!: InsightDetails

	@Prop()
	fields!: CaseColumn[]

	@Prop()
	groupByCalculationFields!: CaseColumn[]

	@Prop()
	calculationFields!: CaseColumn[]

	operators: any[] = operatorLabels;
	private Operator = Operator;

	private CalculationType = CalculationType;
	private CalculationSortItemType = CalculationSortItemType

	calculationTypeValues: any[] = [
		{
			type: CalculationType.Total,
			label: "Total",
			requiresField: true
		},
		{
			type: CalculationType.Count,
			label: "Count",
			requiresField: false
		},
		{
			type: CalculationType.Average,
			label: "Average",
			requiresField: true
		},
		{
			type: CalculationType.Min,
			label: "Minimum",
			requiresField: true
		},
		{
			type: CalculationType.Max,
			label: "Maximum",
			requiresField: true
		},
		{
			type: CalculationType.CountPerDay,
			label: "Count Per Day",
			requiresField: false,
			canUse: () => this.insight.clauses.some(c => c.fieldDataType === DataType.dateTime && c.operator === Operator.GreaterThan)
		}
	]

	@Watch("calculationFields")
	onValueChanged (newCalculationFields: CaseColumn[]) {
		// Remove any selected calculation fields if they no longer exist on the Insight
		if (!newCalculationFields.find(c => c.key === this.value.item.field)) {
			this.value.item.field = undefined
		}
	}

	private onFieldChanged (newValue: string | undefined) {
		this.$emit("calculation-changed", this.value)
	}

	get calculationTypes (): any[] {
		return this.calculationTypeValues.filter(calcType => calcType.canUse === undefined || calcType.canUse())
	}

	get hasInsightGroupBy (): boolean {
		return this.insight && this.insight.groupBy != null && this.insight.groupBy !== ""
	}

	get hasGrouping (): boolean {
		return this.value && this.value.item && this.value.item.groupBy !== null
	}

	set hasGrouping (value: boolean) {
		if (value) {
			// Raise event to show warning dialog for calculations that are used as insight filters
			if (this.tenantEnabledFeatures && this.tenantEnabledFeatures[TenantFeatures.InsightFilterWithCalculations] && this.hasGrouping !== value) {
				this.$emit("filter-calculation-grouped", this.value, "")
			} else if (this.value.item.groupBy == null) {
				Vue.set(this.value.item, "groupBy", "")
			}

			if (this.calculateAsPercentage) {
				Vue.set(this.value.item, "calculatePercentage", false)
			}
		} else {
			Vue.set(this.value.item, "groupBy", null)
		}
		this.$emit("input", this.value)
	}

	get hasExpression (): boolean {
		return this.value && this.value.item && !!this.value.item.expression
	}

	set hasExpression (value: boolean) {
		if (value) {
			if (this.value.item.expression == null) {
				Vue.set(this.value.item, "expression", "{value}")
			}
		} else {
			Vue.set(this.value.item, "expression", null)
		}
		this.$emit("input", this.value)
	}

	get applyFilters (): boolean {
		return this.value && this.value.item && !!this.value.item.applyFilters
	}
	set applyFilters (value: boolean) {
		Vue.set(this.value.item, "applyFilters", value)
		this.$emit("input", this.value)
	}

	get percentageCalculationsFeatureEnabled (): boolean {
		return this.tenantEnabledFeatures && this.tenantEnabledFeatures[TenantFeatures.PercentageCalculations]
	}

	get calculateAsPercentage (): boolean {
		return this.value && this.value.item && !!this.value.item.calculatePercentage
	}

	set calculateAsPercentage (value: boolean) {
		Vue.set(this.value.item, "calculatePercentage", value)
		this.$emit("input", this.value)
	}

	get isValidCalculationTypeForPercentage (): boolean {
		return this.value.item.calcType === CalculationType.Total || this.value.item.calcType === CalculationType.Count
	}

	get calculateAsPercentageTooltip (): string {
		if (!this.hasInsightGroupBy) {
			return "An insight must have a 'group by' set to calculate as a percentage."
		} else if (!this.isValidCalculationTypeForPercentage) {
			return "The selected calculation type is not valid for percentage calculations. The calculation type must be 'Total' or 'Count'."
		}

		let additionalConditions: string[] = []
		if (this.hasGrouping) {
			additionalConditions.push("'Group Calculations'")
		}
		if (this.hasExpression) {
			additionalConditions.push("'Add Expression'")
		}
		if (additionalConditions.length > 0) {
			const verb = additionalConditions.length > 1 ? "are" : "is"
			return `Cannot calculate as a percentage while ${additionalConditions.join(" or ")} ${verb} checked.`
		}
		return ""
	}

	get hasExtendedGrouping (): boolean {
		return this.extendedGroupingOptions.length > 0
	}

	get extendedGroupingOptions (): { value: string, label: string }[] {
		if (!this.value || !this.value.item.groupBy) return []

		const groupBy = this.fields.find(f => f.key === this.value.item.groupBy)
		if (!groupBy) return []

		if (groupBy.dataType === "user-role" &&
			this.tenantEnabledFeatures &&
			this.tenantEnabledFeatures[TenantFeatures.UserRoleLocation]) {
			return [
				{
					value: "user",
					label: "User"
				},
				{
					value: "location",
					label: "Location"
				}
			]
		}

		return []
	}

	private calculatedFieldSupportsInsightCalculations (field: CaseColumn): Boolean {
		if (!field.fieldExtensions) {
			return false
		}

		const calculationDataType = this.getCalculationDataType(field)
		return calculationDataType === "number" || calculationDataType === "time-spent"
	}

	private getCalculationDataTypeFromFieldId (fieldId: string) : string | null {
		const strippedFieldId = this.stripFieldId(fieldId)

		const field = this.getFieldFromFieldId(strippedFieldId)
		if (!field || !field.fieldExtensions) {
			return null
		}
		const calculationDataType = CalculationDataType[field.fieldExtensions.calculationType as CalculationType]
		return calculationDataType
	}

	private stripFieldId (fieldId: string) : string {
		if (fieldId.startsWith("fields.")) {
			return fieldId.substring(7)
		}
		return fieldId
	}

	private getFieldFromFieldId (fieldId: string) : CaseColumn {
		const strippedFieldId = this.stripFieldId(fieldId)
		const field = this.fields.find(c => c.fieldId === strippedFieldId)
		if (!field) {
			Error(`Field ${fieldId} not found`)
		}
		return field!
	}

	private getTimeSpentTrackingField (timeSpentFieldId: string) : CaseColumn {
		const strippedFieldId = this.stripFieldId(timeSpentFieldId)
		const timeSpentField = this.fields.find(c => c.fieldId === strippedFieldId)

		const trackingFieldId = timeSpentField!.fieldExtensions!.trackingFieldId
		const trackingField = this.fields.find(c => c.fieldId === trackingFieldId)

		if (!trackingField) {
			throw new Error(`Could not find tracking field ${trackingFieldId}`)
		}
		return trackingField
	}

	private timeSpentCalculationChange (
		calculation: InsightCalculation,
		timeSpentCalculation: TimeSpentCalculation) {
		calculation.timeSpentCalculation = timeSpentCalculation
	}

	private getCalculationDataType (field: CaseColumn) : string | null {
		if (!field.fieldExtensions) {
			return null
		}
		const calculationDataType = CalculationDataType[field.fieldExtensions.calculationType as CalculationType]
		return calculationDataType
	}

	get calculationNumberColumns () {
		return this.insight.calculations.map(c => {
			return {
				label: c.label,
				key: `calculations[${c.key}]`,
				fieldDataType: DataType.number
			}
		})
	}

	get canFilter () {
		return this.insight.clauses && this.insight.clauses.some(i => i.field === this.value.item.field && i.fieldDataType === DataType.userRole &&
			(i.operator === Operator.Contains || i.operator === Operator.UserTimeLocation))
	}

	private expressionCheckChanged (calculation: InsightCalculation, hasExpression: boolean) {
		if (hasExpression) {
			if (!calculation.expression) {
				calculation.expression = "{value}"
			}
		} else {
			calculation.expression = null
		}
	}

	private groupByChanged () {
		if (this.extendedGroupingOptions.length > 0) {
			if (!this.value.item.groupByExtended) {
				this.value.item.groupByExtended = this.extendedGroupingOptions[0].value
			}
		} else {
			this.value.item.groupByExtended = null
		}
	}

	calculationTypeChanged (newValue: CalculationType | null) {
		if (!this.tenantEnabledFeatures || !this.tenantEnabledFeatures[TenantFeatures.DataEntryAutoFocus]) {
			return
		}
		if (newValue !== null) {
			setTimeout(() => {
				const fieldSelect = (this.$refs[`insightCalculationFieldSelect${this.value.key}`] as Vue).$el.querySelector("input")
				if (fieldSelect) fieldSelect.focus()
			}, TRANSITION_FOCUS_DELAY_MS)
		}
		if (newValue !== CalculationType.Total && newValue !== CalculationType.Count) {
			Vue.set(this.value.item, "calculatePercentage", false)
		}
	}
}

